import { type IReactionDisposer, makeAutoObservable, observable, reaction } from 'mobx'
import React from 'react'
import { type ActionItem } from 'shared/ui'
import { replaceLink } from 'shared/lib'
import { IAIActionTypeEnum } from 'entities/AIAssistant'
import { type IResponseMediaShort } from 'entities/Attachment'
import { Attachment } from 'entities/Attachment/model/Attachment'
import { type MergeField } from 'entities/MergeField/model/MergeField'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { ShortLinkDropdownItem } from 'entities/ShortLink'
import {
  EmojiAction,
  EnumVariantMessageField,
  type IMessageFieldStoreProps,
  makeSavedReplyInputAction,
  mergeFieldReplacer,
  MessageFieldStore,
  SavedRepliesAction,
  ShortenLinkAction,
} from 'widgets/MessageField'
import { MediaAction } from 'widgets/MessageField/ui/FieldActions/MediaAction/MediaAction'
import { MergeFieldsIconAction } from 'widgets/MergeField/ui/actions/MergeFieldsIconAction/MergeFieldsIconAction'
import { AIAssistantAction, AiAssistantStore } from 'widgets/AIAssistant'
import { type IMessageDataParams } from 'widgets/MessageFormFields/SMSMessageField/store/type'
import { SavedRepliesStore } from 'widgets/SavedReplies'
import { type IMergeFieldConfig } from 'widgets/MergeField'
import { RequestReviewDropdownItem } from 'widgets/RequestReview'

export type IActionsConfig = {
  showMedia?: boolean
  showSavedReplies?: boolean
  showEmoji?: boolean
  showAIAssistant?: boolean
  showMergeFields?: boolean
  showShortenLink?: boolean
  showRequestReview?: boolean
}

export type ISMSMessageFieldStoreProps = {
  onFocus: () => void
  isCurrentAirCall?: boolean
  placeholder?: string
  messageFieldStoreProps?: IMessageFieldStoreProps
  mergeFieldsProps?: IMergeFieldConfig
  isMarketing?: boolean
  actionsConfig?: IActionsConfig
}

export class SMSMessageFieldStore {
  messageFieldStore
  aiAssistantStore
  isDisabledObj = observable.object({ value: true })
  private _savedRepliesStore: SavedRepliesStore
  private _disposeMessageInnerText: IReactionDisposer | null = null
  private _actionsConfig: IActionsConfig
  private _isMarketing = false

  constructor(
    {
      onFocus,
      isCurrentAirCall,
      placeholder,
      mergeFieldsProps,
      messageFieldStoreProps,
      isMarketing,
      actionsConfig,
    }: ISMSMessageFieldStoreProps,
    private _entities: IEntitiesStore
  ) {
    this._isMarketing = !!isMarketing
    this._actionsConfig = actionsConfig || {
      showMedia: true,
      showSavedReplies: true,
      showEmoji: true,
      showAIAssistant: true,
      showMergeFields: true,
      showShortenLink: true,
      showRequestReview: true,
    }
    this._savedRepliesStore = new SavedRepliesStore(this._entities, {
      mergeFieldProps: mergeFieldsProps,
    })
    this.messageFieldStore = new MessageFieldStore(
      {
        isCurrentAirCall,
        placeholder: placeholder || 'Write your message...',
        styles: {
          minHeight: 106,
        },
        variant: EnumVariantMessageField.Underline,
        showActionsItems: 5,
        makeInputActions: (store) => [makeSavedReplyInputAction(store, this._savedRepliesStore)],
        makeEvents: () => ({
          onFocus,
        }),
        isShortUrlPostfix: true,
        isMarketing,
        ...messageFieldStoreProps,
      },
      this._entities
    )
    this.aiAssistantStore = new AiAssistantStore(
      this.messageFieldStore,
      {
        actions: [
          IAIActionTypeEnum.Rephrase,
          IAIActionTypeEnum.MoreFriendly,
          IAIActionTypeEnum.MoreFormal,
          IAIActionTypeEnum.Expand,
          IAIActionTypeEnum.Shorten,
        ],
      },
      this._entities
    )
    this._setActions(mergeFieldsProps)

    makeAutoObservable(this)

    this.reactionMessageInnerText()
  }

  dispose = () => {
    this._disposeMessageInnerText?.()
  }

  reactionMessageInnerText = () => {
    this._disposeMessageInnerText?.()
    this._disposeMessageInnerText = reaction(
      () => this.messageFieldStore.messageInnerText,
      (text) => {
        this.isDisabledObj.value = !text
      },
      {
        fireImmediately: true,
      }
    )
  }
  init = (message: string, media_url?: IResponseMediaShort[]) => {
    this.messageFieldStore.setMessageData({
      attachments: media_url?.map((media) => new Attachment({ responseMediaShort: media })) || [],
      message: message || '',
      isFocus: false,
      isReset: false,
      isReplace: true,
    })
  }

  private _setActions = (mergeFieldsProps?: IMergeFieldConfig) => {
    const actions: ActionItem[] = []

    if (this._actionsConfig.showMedia) {
      actions.push({
        iconButtonComponent: <MediaAction />,
      })
    }

    if (this._actionsConfig.showSavedReplies) {
      actions.push({
        iconButtonComponent: <SavedRepliesAction store={this._savedRepliesStore} useHotKey />,
      })
    }

    if (this._actionsConfig.showEmoji) {
      actions.push({
        iconButtonComponent: <EmojiAction />,
      })
    }

    if (this._actionsConfig.showAIAssistant) {
      actions.push({
        iconButtonComponent: (
          <AIAssistantAction
            aiAssistantStore={this.aiAssistantStore}
            placementMainContent={'bottom-start'}
            isDisabledObj={this.isDisabledObj}
          />
        ),
      })
    }

    if (this._actionsConfig.showMergeFields) {
      actions.push({
        iconButtonComponent: (
          <MergeFieldsIconAction
            onAddMergeField={(value) => this.onAddMergeField(value)}
            isAllConnectedOrDisconnectedIntegration
            {...mergeFieldsProps}
          />
        ),
      })
    }

    if (this._actionsConfig.showShortenLink) {
      actions.push({
        iconButtonComponent: <ShortenLinkAction />,
        dropdownItemComponent: (onCloseMenu) => (
          <ShortLinkDropdownItem
            onOpenModal={() => {
              const messageInnerText = this.messageFieldStore.messageInnerText
              this._entities.shortLinkStore.setIsMarketing(this._isMarketing)
              this._entities.shortLinkStore.setMessageText(messageInnerText)
            }}
            onAddShortLink={(link) => {
              this.messageFieldStore.addContent &&
                this.messageFieldStore.addContent(replaceLink(link) + '&nbsp;')
            }}
            onCloseMenu={onCloseMenu}
          />
        ),
      })
    }

    if (this._actionsConfig.showRequestReview && this._entities.featureFlagsStore.reviews) {
      actions.push({
        dropdownItemComponent: () => <RequestReviewDropdownItem />,
      })
    }

    this.messageFieldStore?.setActions(actions)
  }

  get isError() {
    return (
      this.messageFieldStore.textLimitReached ||
      this.messageFieldStore.isAttachmentsCountLimitError ||
      (this.messageFieldStore.isAirCallNoSupportMms &&
        !this._entities.featureFlagsStore.aircall_new_api)
    )
  }

  get segments_count_rcs() {
    return this.messageFieldStore.messageRemainsInfoRCSandMMS.messageCount
  }

  get segments_count() {
    return this.messageFieldStore.messageRemainsInfo.messageCount
  }

  onAddMergeField = (mergeField: MergeField) => {
    if (this.messageFieldStore.addContent) {
      this.messageFieldStore.addContent(
        mergeFieldReplacer(this._entities, mergeField.template) + '&nbsp;'
      )
    }
  }

  get messageDataParams(): IMessageDataParams {
    return {
      media_url: this.messageFieldStore.media_url.map((item) => item.attachmentDraft),
      message: this.messageFieldStore.messageText,
    }
  }
}
